import React from 'react';
import { useParams } from 'react-router-dom';
import useHttp from '../../../hooks/http.hook';

const EMPTY_STATS = {
  soldProducts: '—',
  placedProducts: '—',
  totalOrders: '—',
  canceledOrders: '—',
  investmentAmount: '—',
  incomeAmount: '—',
  expenseAmount: '—',
};

const STAT_CARDS = [
  { key: 'soldProducts', title: 'Продано шт.' },
  { key: 'placedProducts', title: 'Оформлено шт.' },
  { key: 'totalOrders', title: 'Заказы' },
  { key: 'canceledOrders', title: 'Отменено' },
  { key: 'investmentAmount', title: 'Инвестиции' },
  { key: 'incomeAmount', title: 'Выручка' },
  { key: 'expenseAmount', title: 'Расходы' },
];

const ANALYTICS_CARDS = [
  { key: 'soldQuantity', title: 'Продано шт.', accent: 'blue', money: false },
  { key: 'placedQuantity', title: 'Оформлено шт.', accent: 'teal', money: false },
  { key: 'totalOrders', title: 'Заказы', accent: 'purple', money: false },
  { key: 'canceledOrders', title: 'Отменено', accent: 'slate', money: false },
  { key: 'investmentAmount', title: 'Инвестиции', accent: 'indigo', money: true },
  { key: 'soldRevenue', title: 'Выручка', accent: 'green', money: true },
  { key: 'expenseAmount', title: 'Расходы', accent: 'orange', money: true },
];

const EMPTY_RAW = {
  soldQuantity: 0,
  placedQuantity: 0,
  soldRevenue: 0,
  expenseAmount: 0,
  investmentAmount: 0,
  totalOrders: 0,
  canceledOrders: 0,
};

const formatStatNumber = (value) => {
  const n = Number(value ?? 0);
  return new Intl.NumberFormat('ru-RU', {
    useGrouping: true,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(n) ? n : 0);
};

function analyticsFooter(key, raw) {
  const sold = Number(raw.soldQuantity ?? 0);
  const orders = Number(raw.totalOrders ?? 0);
  const revenue = Number(raw.soldRevenue ?? 0);
  const expense = Number(raw.expenseAmount ?? 0);
  const investment = Number(raw.investmentAmount ?? 0);
  const canceled = Number(raw.canceledOrders ?? 0);
  const whole = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });
  const oneDecimal = new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  if (key === 'soldQuantity' || key === 'placedQuantity') return 'За выбранный период';
  if (key === 'soldRevenue') {
    if (orders <= 0) return 'Нет заказов';
    return `Средний чек ~${whole.format(Math.round(revenue / orders))}`;
  }
  if (key === 'investmentAmount') {
    return investment === 0 ? 'Нет данных' : 'За выбранный период';
  }
  if (key === 'expenseAmount') {
    return expense === 0 ? 'Нет данных' : 'За выбранный период';
  }
  if (key === 'totalOrders') {
    if (orders <= 0) return 'Нет заказов';
    return `${oneDecimal.format(sold / orders)} шт / клиента`;
  }
  if (key === 'canceledOrders') {
    if (orders <= 0) return '0% от заказов';
    return `${oneDecimal.format((canceled / orders) * 100)}% от заказов`;
  }
  return '';
}

function StatsGrid({ periodId = 1, variant = 'default', onLoaded }) {
  const { requestWithMeta } = useHttp();
  const params = useParams();
  const statsPromiseRef = React.useRef(null);
  const lastRequestFnRef = React.useRef(null);
  const lastBranchKeyRef = React.useRef(null);
  const lastPeriodIdRef = React.useRef(null);
  const onLoadedRef = React.useRef(onLoaded);
  const [loading, setLoading] = React.useState(true);

  const [stats, setStats] = React.useState(EMPTY_STATS);
  const [rawStats, setRawStats] = React.useState(EMPTY_RAW);

  onLoadedRef.current = onLoaded;

  React.useEffect(() => {
    let cancelled = false;

    const branchKey = params.branchId || 'store';
    const period = Number(periodId) || 1;

    // If auth/token changes, requestWithMeta callback identity changes.
    // In that case, clear cached promise so we refetch with new credentials.
    if (lastRequestFnRef.current !== requestWithMeta) {
      lastRequestFnRef.current = requestWithMeta;
      statsPromiseRef.current = null;
      lastBranchKeyRef.current = branchKey;
      lastPeriodIdRef.current = period;
    }

    // If route branch or period changes, refetch stats.
    if (lastBranchKeyRef.current !== branchKey || lastPeriodIdRef.current !== period) {
      lastBranchKeyRef.current = branchKey;
      lastPeriodIdRef.current = period;
      statsPromiseRef.current = null;
    }

    setLoading(true);
    setStats(EMPTY_STATS);

    const statsPromise =
      statsPromiseRef.current ??
      (statsPromiseRef.current = requestWithMeta(
        `/operator/stats/branch/${branchKey}?periodId=${period}`,
        'GET'
      ));

    (async () => {
      try {
        const res = await statsPromise;
        if (cancelled) return;
        if (!res?.ok) return;

        const data = res?.data;
        const s = data?.stats || {};

        setStats({
          soldProducts: formatStatNumber(s?.soldQuantity),
          placedProducts: formatStatNumber(s?.placedQuantity),
          totalOrders: formatStatNumber(s?.totalOrders),
          canceledOrders: formatStatNumber(s?.canceledOrders),
          investmentAmount: formatStatNumber(s?.investmentAmount),
          incomeAmount: formatStatNumber(s?.soldRevenue),
          expenseAmount: formatStatNumber(s?.expenseAmount),
        });
        setRawStats({
          soldQuantity: Number(s?.soldQuantity ?? 0) || 0,
          placedQuantity: Number(s?.placedQuantity ?? 0) || 0,
          soldRevenue: Number(s?.soldRevenue ?? 0) || 0,
          expenseAmount: Number(s?.expenseAmount ?? 0) || 0,
          investmentAmount: Number(s?.investmentAmount ?? 0) || 0,
          totalOrders: Number(s?.totalOrders ?? 0) || 0,
          canceledOrders: Number(s?.canceledOrders ?? 0) || 0,
        });
      } catch (_) {
        // ignore for now (view-first app)
      } finally {
        if (!cancelled) {
          setLoading(false);
          onLoadedRef.current?.();
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [requestWithMeta, params.branchId, periodId]);

  const cards = variant === 'analytics' ? ANALYTICS_CARDS : STAT_CARDS;

  return (
    <section
      className={variant === 'analytics' ? 'main-page-stats analytics-kpis' : 'main-page-stats'}
      aria-label="Stats"
      aria-busy={loading}
    >
      {loading
        ? cards.map((card) => (
            <div
              key={card.key}
              className={
                variant === 'analytics'
                  ? `main-page-statCard main-page-statCard--skeleton analytics-kpi analytics-kpi--${card.accent}`
                  : 'main-page-statCard main-page-statCard--skeleton'
              }
              aria-hidden="true"
            >
              <div className="main-page-skeleton main-page-skeleton--text main-page-statTitleSkeleton" />
              <div className="main-page-skeleton main-page-skeleton--text main-page-statValueSkeleton" />
              {variant === 'analytics' ? (
                <div className="main-page-skeleton main-page-skeleton--text analytics-kpiFootSkeleton" />
              ) : null}
            </div>
          ))
        : variant === 'analytics'
          ? ANALYTICS_CARDS.map((card) => (
              <div
                key={card.key}
                className={`main-page-statCard analytics-kpi analytics-kpi--${card.accent}`}
              >
                <div className="main-page-statTitle">{card.title}</div>
                <div className="main-page-statValue">
                  {formatStatNumber(rawStats[card.key])}
                  {card.money ? <small className="analytics-kpiUnit">сом</small> : null}
                </div>
                <div className="analytics-kpiFoot">
                  <span className="analytics-kpiDot" />
                  {analyticsFooter(card.key, rawStats)}
                </div>
              </div>
            ))
          : STAT_CARDS.map((card) => (
              <div key={card.key} className="main-page-statCard">
                <div className="main-page-statTitle">{card.title}</div>
                <div className="main-page-statValue">{stats?.[card.key] ?? '—'}</div>
              </div>
            ))}
    </section>
  );
}

export default StatsGrid;
